import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { ApiError, toAppError, TWO_FACTOR_STEP_UP_REQUIRED } from '@repo/api-client';
import { getErrorMessage } from '@repo/i18n';
import { emitToast } from '../components/ui/Toast';
import { reportWebError } from './error-reporter';

export const AUTH_LOGOUT_EVENT = 'auth:logout';
export const AUTH_FORBIDDEN_EVENT = 'auth:forbidden';
export const NETWORK_ERROR_EVENT = 'network:error';

export function emitLogout() {
  window.dispatchEvent(new Event(AUTH_LOGOUT_EVENT));
}

// Per reference/UI-GOVERNANCE.md §6.2: `validation` is handled inline on the
// form (field errors + InlineAlert) — it never gets a toast here, or the two
// surfaces fight over the same failure.
function handleGlobalError(error: unknown) {
  reportWebError(error);

  if (error instanceof TypeError && error.message.includes('fetch')) {
    window.dispatchEvent(new Event(NETWORK_ERROR_EVENT));
    return;
  }

  if (error instanceof ApiError) {
    const appError = toAppError(error);
    if (appError.category === 'validation') return;

    if (appError.category === 'unauthorized') {
      // RG-001: a step-up challenge the user declined is not a dead session — logging them out
      // here would defeat the point of an in-session proof. The dialog has already been shown;
      // just explain that the action did not go through.
      if (error instanceof ApiError && error.code === TWO_FACTOR_STEP_UP_REQUIRED) {
        emitToast({
          type: 'warning',
          title: 'Verification Required',
          description: 'Enter your authenticator code to continue.',
        });
        return;
      }

      emitToast({
        type: 'error',
        title: 'Session Expired',
        description: getErrorMessage(appError),
      });
      emitLogout();
      return;
    }

    if (appError.category === 'forbidden') {
      emitToast({
        type: 'error',
        title: 'Not Permitted',
        description: getErrorMessage(appError),
      });
      return;
    }

    emitToast({
      type: 'error',
      title: 'Error',
      description: getErrorMessage(appError),
    });
    return;
  }

  if (error instanceof Error) {
    emitToast({
      type: 'error',
      title: 'Application Error',
      description: error.message,
    });
  }
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      handleGlobalError(error);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // Per D-004: Deduplicate error toasts. If the mutation defines its own local onError
      // handler or explicitly requests global error suppression via meta, skip the global
      // toast so only a single user-facing error notification is emitted.
      if (
        mutation?.options?.onError ||
        (mutation?.meta as { suppressGlobalError?: boolean } | undefined)?.suppressGlobalError
      ) {
        return;
      }
      handleGlobalError(error);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      throwOnError: false,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && (error.status === 401 || error.status === 403 || error.status === 404)) {
          return false;
        }
        if (error instanceof TypeError && error.message.includes('fetch')) {
          return failureCount < 3;
        }
        if (error instanceof ApiError && error.status === 502) {
          return failureCount < 3;
        }
        return failureCount < 1;
      },
      retryDelay: (failureCount) => {
        if (failureCount <= 1) return 1000;
        return 2000;
      },
    },
  },
});

export const sessionQueryKey = ['session'] as const;
